"use client";

import { useEffect, useRef } from "react";
import { foldText } from "./publisher-change-span";
import {
  MAX_INSTANCES,
  MAX_LABEL_LENGTH,
  isSafeLocator,
  isSafeSnippets,
  type SnippetInstance,
} from "./publisher-nav-protocol";

const ATTR = "data-publisher-highlight";
// Set on <html> while the tray's "Highlight changes" toggle is off. Matching
// still runs and still reports the places it found — only the paint is
// suppressed — so the tray lists the same rows either way.
const UNPAINTED_ATTR = "data-publisher-highlight-unpainted";
const PULSE_ATTR = "data-publisher-highlight-pulse";
// A separate set from ATTR: these elements show copy an in-flight edit is about
// to replace, so they wear the shimmer until the edit lands. Once it does, the
// element re-matches as a plain ATTR highlight (a solid box, no shimmer).
const LOADING_ATTR = "data-publisher-highlight-loading";
const SHIMMER_LAYER_ID = "publisher-highlight-shimmer-layer";
const SHIMMER_CLASS = "publisher-highlight-shimmer";
// The section-build placeholder: an in-flow box that stands in for the section
// the agent is building right now, sitting where that section will render.
const BUILDING_ID = "publisher-building-placeholder";
// The previewed pages label their hero for analytics. A hero that stays put
// while the page scrolls over it can't be scrolled to — it is already on
// screen, behind the page — so its changes go to the top instead.
const HERO_SELECTOR =
  '[data-analytics-region="Hero"], [data-analytics-region="hero"]';
const STYLE_ID = "publisher-highlight-style";
const REAPPLY_DEBOUNCE_MS = 150;
const PULSE_MS = 2400;
// Covers a reveal that arrives before a cold-compile route (or late
// hydration) has painted the target: one retry, then give up quietly.
const REVEAL_RETRY_MS = 250;
// A needle is a whole run of edited copy, so it normally resolves to one
// element — but a shared value can legitimately render in every card of a long
// grid, and those are exactly the places the tray exists to show. High enough
// not to bite a real page, and the overflow is dropped from the tail rather
// than costing the snippet every place it reached.
const MAX_HITS_PER_SNIPPET = 200;
// Text inside these never renders visibly, but Next.js embeds the full page
// copy verbatim in RSC flight-data <script> tags, so every snippet would
// otherwise always match one.
const SKIPPED_ANCESTORS = "script, style, noscript, template";
const HEADINGS = "h1, h2, h3, h4, h5, h6";

// --color-fin is a fin-ai token; intercom-com does not necessarily define it,
// so the literal is the fallback rather than a nicety.
const CSS = `
:root:not([${UNPAINTED_ATTR}]) [${ATTR}], [${LOADING_ATTR}] {
  outline: 2px solid var(--color-fin, #ff5600) !important;
  /* Inset, not outset: an outline drawn outside the border box is clipped by
     any ancestor with overflow:hidden — routine on marketing heroes — so the
     ring only survived at widths that happened to leave slack. */
  outline-offset: -2px !important;
  border-radius: 7px !important;
  background-color: color-mix(in oklab, var(--color-fin, #ff5600) 10%, transparent) !important;
}
[${PULSE_ATTR}] {
  animation: publisher-highlight-pulse 0.6s ease-out 3;
}
@keyframes publisher-highlight-pulse {
  50% { outline-color: transparent; }
}
/* The sheen is drawn on a detached overlay laid over each in-flight element's
   on-screen box, never on the element itself, so it can't touch the element's
   own background, animation, or ::after — a highlighted quote keeps its quote
   mark, a struck price keeps its line. soft-light blends the shine into the box
   rather than flatly painting over the text. Bright white waves drift one tile
   per cycle, feathered to transparent at both edges so the repeat has no seam. */
#${SHIMMER_LAYER_ID} {
  position: fixed;
  inset: 0;
  z-index: 2147483646;
  pointer-events: none;
  overflow: hidden;
}
.${SHIMMER_CLASS} {
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 7px;
  pointer-events: none;
  mix-blend-mode: soft-light;
  will-change: transform;
  background-image: linear-gradient(
    110deg,
    transparent 0%,
    rgba(255, 255, 255, 0.375) 30%,
    rgba(255, 255, 255, 0.835) 50%,
    rgba(255, 255, 255, 0.375) 70%,
    transparent 100%
  );
  background-size: 680px 100%;
  background-repeat: repeat;
  animation: publisher-highlight-shimmer 6s linear infinite;
}
@keyframes publisher-highlight-shimmer {
  from { background-position: 0 0; }
  to { background-position: 680px 0; }
}
/* Unlike the sheen above, the build placeholder has no element behind it to
   blend into, so it paints its own fin-tinted box (matching a settled highlight)
   and runs the same sweep with soft-light dropped so the shine reads on the
   empty box. It sits in the normal flow where the next section will land. */
#${BUILDING_ID} {
  min-height: 200px;
  margin: 24px auto;
  border-radius: 7px;
  outline: 2px solid var(--color-fin, #ff5600);
  outline-offset: -2px;
  background-color: color-mix(in oklab, var(--color-fin, #ff5600) 10%, transparent);
  background-image: linear-gradient(
    110deg,
    transparent 0%,
    rgba(255, 255, 255, 0.35) 30%,
    rgba(255, 255, 255, 0.85) 50%,
    rgba(255, 255, 255, 0.35) 70%,
    transparent 100%
  );
  background-size: 680px 100%;
  background-repeat: repeat;
  animation: publisher-highlight-shimmer 2.5s linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  #${BUILDING_ID} { animation: none; }
}
`;

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

function removeStyle(): void {
  document.getElementById(STYLE_ID)?.remove();
}

function setPainted(painted: boolean): void {
  if (painted) document.documentElement.removeAttribute(UNPAINTED_ATTR);
  else document.documentElement.setAttribute(UNPAINTED_ATTR, "");
}

const shimmerNodes = new Map<Element, HTMLElement>();

// The overlay lives under <html>, not <body>: the mutation observer watches the
// body subtree, and our own node writes there would re-trigger it in a loop.
function ensureShimmerLayer(): HTMLElement {
  let layer = document.getElementById(SHIMMER_LAYER_ID);
  if (!layer) {
    layer = document.createElement("div");
    layer.id = SHIMMER_LAYER_ID;
    document.documentElement.appendChild(layer);
  }
  return layer;
}

// Lay one sheen box over each in-flight element's on-screen rect. Nodes are
// reused across calls so repositioning on scroll/resize never restarts the
// sweep mid-animation; a rect that has collapsed to zero is hidden, not removed.
function positionShimmers(): void {
  const marked = new Set(document.querySelectorAll(`[${LOADING_ATTR}]`));
  shimmerNodes.forEach((node, el) => {
    if (!marked.has(el)) {
      node.remove();
      shimmerNodes.delete(el);
    }
  });
  if (!marked.size) return;
  const layer = ensureShimmerLayer();
  marked.forEach((el) => {
    const rect = el.getBoundingClientRect();
    let node = shimmerNodes.get(el);
    if (!node) {
      node = document.createElement("div");
      node.className = SHIMMER_CLASS;
      layer.appendChild(node);
      shimmerNodes.set(el, node);
    }
    node.style.transform = `translate(${rect.left}px, ${rect.top}px)`;
    node.style.width = `${rect.width}px`;
    node.style.height = `${rect.height}px`;
    node.style.visibility = rect.width && rect.height ? "" : "hidden";
  });
}

function clearShimmers(): void {
  shimmerNodes.forEach((node) => node.remove());
  shimmerNodes.clear();
}

function removeShimmerLayer(): void {
  document.getElementById(SHIMMER_LAYER_ID)?.remove();
}

function hideBuildingPlaceholder(): void {
  document.getElementById(BUILDING_ID)?.remove();
}

// Stand the placeholder directly after the last section already on the page —
// every section carries data-analytics-region, and the agent inserts the next
// one at exactly that spot, so the box marks where the incoming component lands.
// Only section roots count: a section can nest its own data-analytics-region
// elements, so filter to the top-level ones (no such ancestor) — otherwise the
// box would anchor to a region deep inside the last section and land within it.
// Re-anchored on each call because a committed section reloads the frame and the
// newest section becomes the last one. Skipped when nothing has rendered yet: the
// parent's full-pane build overlay still covers the preview and there is no
// section to anchor to.
function showBuildingPlaceholder(selector = "[data-analytics-region]"): void {
  const sections = [...document.querySelectorAll(selector)].filter(
    (el) =>
      el.id !== BUILDING_ID && el.parentElement?.closest(selector) == null,
  );
  const anchor = sections[sections.length - 1];
  if (!anchor?.parentNode) {
    hideBuildingPlaceholder();
    return;
  }
  let node = document.getElementById(BUILDING_ID);
  if (!node) {
    node = document.createElement("div");
    node.id = BUILDING_ID;
    node.setAttribute("aria-hidden", "true");
  }
  if (node.previousElementSibling !== anchor) {
    anchor.parentNode.insertBefore(node, anchor.nextSibling);
  }
}

// PULSE_ATTR is deliberately left alone: it's owned by reveal()'s own timer,
// so a re-apply mid-pulse (toggle flip, mutation-observer tick) can't erase
// an animation that's already in flight.
function clear(): void {
  document.querySelectorAll(`[${ATTR}]`).forEach((el) => {
    el.removeAttribute(ATTR);
  });
}

function clearLoading(): void {
  document.querySelectorAll(`[${LOADING_ATTR}]`).forEach((el) => {
    el.removeAttribute(LOADING_ATTR);
  });
  clearShimmers();
}

// Visible text per element, with script/style subtrees pruned rather than
// filtered later: Next.js embeds the whole page copy verbatim in RSC flight
// data, so an unpruned document.body contains every needle twice over.
function visibleText(root: Element): Map<Element, string> {
  const texts = new Map<Element, string>();
  const walk = (el: Element): string => {
    if (el.matches(SKIPPED_ANCESTORS)) return "";
    let text = "";
    for (const child of el.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) text += child.textContent ?? "";
      else if (child.nodeType === Node.ELEMENT_NODE)
        text += walk(child as Element);
    }
    texts.set(el, foldText(text));
    return text;
  };
  walk(root);
  return texts;
}

function hasLayout(): boolean {
  const rect = document.body.getBoundingClientRect?.();
  return !!rect && (rect.width > 0 || rect.height > 0);
}

// A responsive page keeps a desktop and a mobile copy of the same row in the
// DOM and hides one, which would earn a tray row that scrolls nowhere.
// checkVisibility alone missed those, so the rendered box decides — and where
// checkVisibility is absent the box still decides on its own, since a
// display:none duplicate measures zero. Fails open only where nothing can be
// measured: a spare row beats losing a real one.
function isVisible(el: Element): boolean {
  const check = (el as Element & { checkVisibility?: () => boolean })
    .checkVisibility;
  if (typeof check === "function" && !check.call(el)) return false;
  if (!hasLayout()) return true;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
}

// Outlasts the 500ms open animation and a single-open accordion's collapse above.
const REVEAL_SETTLE_MS = 600;

type ClosedDisclosure = { panel: Element; opener: HTMLElement };

// Base UI's closed triggers carry no aria-controls, so the header is found by position.
function collapsedTrigger(panel: Element): HTMLElement | null {
  if (!panel.matches('[role="region"][hidden]')) return null;
  const trigger = panel.previousElementSibling?.querySelector(
    '[aria-expanded="false"]',
  );
  return trigger instanceof HTMLElement ? trigger : null;
}

// Every closed disclosure ancestor up to the document root, innermost first —
// an open one in between is skipped, not a stopping point.
function closedDisclosureAncestors(el: Element): ClosedDisclosure[] {
  const chain: ClosedDisclosure[] = [];
  for (let node: Element | null = el; node; node = node.parentElement) {
    const trigger = collapsedTrigger(node);
    if (trigger) chain.push({ panel: node, opener: trigger });
    else if (node instanceof HTMLDetailsElement && !node.open)
      chain.push({ panel: node, opener: node });
  }
  return chain;
}

// Excludes a responsive display:none duplicate that happens to sit inside a
// closed panel from wrongly counting as revealable.
function hiddenBeforeBoundary(el: Element, chain: ClosedDisclosure[]): boolean {
  if (!hasLayout()) return false;
  const panels = new Set(chain.map((disclosure) => disclosure.panel));
  const boundary = chain[chain.length - 1].panel;
  let node: Element | null = el;
  while (node && node !== boundary) {
    const style = getComputedStyle(node);
    const hidden = style.display === "none" || style.visibility === "hidden";
    if (hidden && !panels.has(node)) return true;
    node = node.parentElement;
  }
  return false;
}

// Not visible only because a disclosure ancestor is closed: the closed
// disclosures to open, outermost first, or null if plainly hidden.
function revealableChain(el: Element): ClosedDisclosure[] | null {
  if (isVisible(el)) return null;
  const innermostFirst = closedDisclosureAncestors(el);
  if (!innermostFirst.length) return null;
  if (!isVisible(innermostFirst[innermostFirst.length - 1].opener)) return null;
  if (hiddenBeforeBoundary(el, innermostFirst)) return null;
  return innermostFirst.reverse();
}

function isShown(el: Element): boolean {
  return isVisible(el) || revealableChain(el) !== null;
}

function textOf(el: Element | null): string {
  // Separator, not bare textContent: adjacent spans in a headline have no
  // whitespace between them, so concatenating gives "One AI Agent.ForGmail."
  const raw = [...(el?.childNodes ?? [])]
    .map((n) => n.textContent ?? "")
    .join(" ");
  return raw.replace(/\s+/g, " ").trim();
}

// A heading is only a useful name for a place if it reads as a label. This page
// wraps a 200-character paragraph in an <h2>, so length is the test, not the tag.
const MAX_HEADING_LABEL = 48;

// Open or closed alike, so a row keeps its name as a single-open accordion
// toggles the item under it.
const DISCLOSURE_PANEL = 'details, [role="region"]';

function disclosureTitle(panel: Element): string {
  if (panel instanceof HTMLDetailsElement) {
    return textOf(panel.querySelector(":scope > summary"));
  }
  const header = panel.previousElementSibling;
  return header?.querySelector("[aria-expanded]") ? textOf(header) : "";
}

// Names the place in the words the user would use for it. Landmarks come first
// because they survive whatever the page's markup does — /channels wraps body
// prose in an <h2> and builds its headline from unspaced spans.
function labelFor(el: Element): string {
  const panel = el.closest(DISCLOSURE_PANEL);
  const title = panel ? disclosureTitle(panel) : "";
  if (title) return `Dropdown "${title}"`.slice(0, MAX_LABEL_LENGTH);

  if (el.closest("h1")) return "Main headline";
  if (el.closest("nav")) return "Navigation menu";
  if (el.closest("footer")) return "Footer";

  const heading = el.closest(HEADINGS);
  const headingText = textOf(heading);
  if (heading && headingText && headingText.length <= MAX_HEADING_LABEL) {
    return headingText.slice(0, MAX_LABEL_LENGTH);
  }
  if (heading) return "Subheading";

  const control = el.closest("button, a");
  const controlText = textOf(control);
  if (control && controlText) {
    const kind = control.tagName === "A" ? "Link" : "Button";
    return `${kind} "${controlText}"`.slice(0, MAX_LABEL_LENGTH);
  }

  // Deliberately not the nearest section heading: on /channels that heading is
  // the rotating hero, so a row named from it renamed itself every few seconds.
  return "Body text";
}

// Where the element sits in the document, as a selector querySelector can
// resolve later. Position-in-document is stable across the animation frames
// that renumber a snippet's matches; position-among-matches is not.
function locatorFor(el: Element): string {
  const parts: string[] = [];
  let node: Element | null = el;
  while (node) {
    if (node === document.body) {
      parts.unshift("body");
      break;
    }
    const parent: Element | null = node.parentElement;
    if (!parent) break;
    const at = [...parent.children].indexOf(node) + 1;
    parts.unshift(`${node.tagName.toLowerCase()}:nth-child(${at})`);
    node = parent;
  }
  return parts.join(" > ");
}

type MarkedElement = {
  snippet: string;
  /** The place's address and its identity — see identityElement. */
  locator: string;
  label: string;
};

// The place a snippet marks is the matched element itself: each repeated list
// item, control, or line that carries the value is its own place, so two of
// them stay two rows instead of collapsing to a shared ancestor.
//
// A cycling hero (e.g. /channels) is the one exception. It swaps a fresh node
// in for each channel and, mid-transition, mounts the leaving and arriving
// slide at once, each in its own "For ___" line — but the hero is one place
// regardless of which slide shows, so a match inside the hero landmark resolves
// to its enclosing heading, giving every slide a single stable identity.
const HEADING_SELECTOR = "h1, h2, h3, h4, h5, h6";
function identityElement(el: Element): Element {
  if (el.closest(HERO_SELECTOR)) {
    const heading = el.closest(HEADING_SELECTOR);
    if (heading) return heading;
  }
  return el;
}

// Matches whole elements rather than individual text nodes is what lets a
// needle be a full run of copy: textContent flattens the inline markup (<br>,
// <em>, a nested <span>) that splits the same sentence across several text
// nodes, and a longer needle is specific enough to land on one element. Returns
// one {snippet, el} pair per matched element, in document order, so the solid
// highlight and the loading shimmer ring exactly the same places.
function matchElements(
  snippets: readonly string[],
): Array<{ snippet: string; el: Element }> {
  // Indexed, not keyed by needle: two snippets can fold to the same needle,
  // and a Map would silently merge their counts.
  const targets = snippets
    .map((snippet) => ({ snippet, needle: foldText(snippet) }))
    .filter((target) => target.needle);
  if (!targets.length) return [];

  const texts = [...visibleText(document.body)];
  const out: Array<{ snippet: string; el: Element }> = [];
  for (const { snippet, needle } of targets) {
    // An element whose entire text IS the changed run is the element that
    // changed. One that merely contains it is usually unrelated copy that
    // happens to quote the same phrase - a new h1 of "the only helpdesk" is a
    // substring of the paragraph "Intercom is the only helpdesk with...".
    // Only fall back to containment when nothing matches outright, which is
    // the case for a run that is one sentence of a longer block.
    const exact = texts.filter(([, text]) => text === needle);
    // A pinned hero (e.g. /channels) shows a shared value inside a longer line
    // ("For Gmail.") while the same value also renders exactly elsewhere; keep
    // the hero-landmark contains match so the exact one doesn't suppress it.
    const heroContains = exact.length
      ? texts.filter(
          ([el, text]) =>
            text !== needle &&
            text.includes(needle) &&
            el.closest(HERO_SELECTOR),
        )
      : [];
    const matched = (
      exact.length
        ? [...exact, ...heroContains]
        : texts.filter(([, text]) => text.includes(needle))
    ).map(([el]) => el);
    // Every ancestor of a match contains the needle too, so keep only the
    // innermost - otherwise one edit rings its element, its section, main
    // and body all at once.
    const innermost = matched
      .filter(
        (el) => !matched.some((other) => other !== el && el.contains(other)),
      )
      .filter(isShown);
    // A run unique among extracted text nodes can still sit inside several
    // unrelated elements, so a containment match landing on more than one is
    // ambiguous - ring none rather than every one. An exact match that repeats
    // is genuine duplicated copy and still outlines each place.
    if (!exact.length && innermost.length > 1) continue;
    for (const el of innermost.slice(0, MAX_HITS_PER_SNIPPET))
      out.push({ snippet, el });
  }
  return out;
}

// The settled highlight: a solid box, no shimmer. Returns an entry per outlined
// element, in document order, so each place one commit reached can become its
// own tray row. In-flight beats settled: an element already wearing a shimmer is
// mid re-edit, so leave the sheen and skip the box — the settled highlight is
// for copy that is done changing, not copy about to change again. Order-free
// with applyLoading, which force-steals the box the other way, so the two can
// arrive in either message and still land on shimmer for a live edit.
export function apply(snippets: readonly string[]): MarkedElement[] {
  clear();
  // An element two snippets both matched is reported once, under the first of
  // them, so the tray can never list the same place on the page twice.
  const claimed = new Set<Element>();
  const places = new Set<string>();
  const marked: MarkedElement[] = [];
  for (const { snippet, el } of matchElements(snippets)) {
    if (!el.hasAttribute(LOADING_ATTR)) el.setAttribute(ATTR, "");
    if (claimed.has(el)) continue;
    claimed.add(el);
    // Two nodes can share one place: mid-transition a hero mounts the leaving
    // and arriving slide at once, but both resolve to the one heading, so the
    // second node outlines without earning a second row.
    const locator = locatorFor(identityElement(el));
    if (places.has(locator)) continue;
    places.add(locator);
    marked.push({ snippet, locator, label: labelFor(el) });
  }
  return marked;
}

// The loading counterpart: the same match, but the elements wear LOADING_ATTR
// and a sheen overlay. In-flight beats settled, so it force-steals the box off a
// matched element (a re-edit of copy a prior commit already highlighted must
// shimmer, not sit as a stale box). The LOADING_ATTR set is rebuilt each call
// and positionShimmers reconciles the overlay against it, reusing a node for an
// element that stayed marked so a re-apply mid-stream never restarts the sweep.
function applyLoading(snippets: readonly string[]): void {
  document.querySelectorAll(`[${LOADING_ATTR}]`).forEach((el) => {
    el.removeAttribute(LOADING_ATTR);
  });
  for (const { el } of matchElements(snippets)) {
    el.removeAttribute(ATTR);
    el.setAttribute(LOADING_ATTR, "");
  }
  positionShimmers();
}

// Tries each snippet in order against the already-marked elements and returns
// the match at `index` — a commit groups several edits, and an earlier one
// (often styling) can be unmatchable while a later one would have worked (I6).
// A row that named a locator resolves to that place or to nothing at all,
// never to a different occurrence of the same words. A positional address can
// come to point at unrelated copy after the DOM shifts, so a resolved element
// that no longer holds the words reveals nothing rather than ringing it — the
// hero excepted, whose word rotates out of an identity that stays put.
function findRevealTarget(
  snippets: readonly string[],
  locator?: string,
  hiddenOnly = false,
): Element | null {
  if (locator) {
    const el = document.querySelector(locator);
    if (!el || el.closest(HERO_SELECTOR)) return el;
    const holdsWords = snippets.some((snippet) => {
      const needle = foldText(snippet);
      return needle && foldText(el.textContent ?? "").includes(needle);
    });
    return holdsWords ? el : null;
  }
  const marked = [...document.querySelectorAll(`[${ATTR}]`)];
  for (const snippet of snippets) {
    const needle = foldText(snippet);
    if (!needle) continue;
    const el = marked.find(
      (candidate) =>
        foldText(candidate.textContent ?? "").includes(needle) &&
        (!hiddenOnly || revealableChain(candidate) !== null),
    );
    if (el) return el;
  }
  return null;
}

function pulse(el: Element): ReturnType<typeof setTimeout> {
  if (el.closest(HERO_SELECTOR))
    window.scrollTo({ top: 0, behavior: "smooth" });
  else el.scrollIntoView({ block: "center", behavior: "smooth" });
  el.setAttribute(PULSE_ATTR, "");
  return setTimeout(() => el.removeAttribute(PULSE_ATTR), PULSE_MS);
}

// The pulse attribute outlives its timer when a new reveal clears it, and
// re-adding an attribute that is already there restarts no animation — so the
// same row clicked twice would ring once. Drop any stale ring first.
function clearPulse(): void {
  document.querySelectorAll(`[${PULSE_ATTR}]`).forEach((el) => {
    el.removeAttribute(PULSE_ATTR);
  });
}

// Outermost first, so a nested panel's trigger is already on-screen when clicked.
function openDisclosureChain(chain: ClosedDisclosure[]): void {
  for (const { opener } of chain) {
    if (opener instanceof HTMLDetailsElement) opener.open = true;
    else if (opener.getAttribute("aria-expanded") === "false") opener.click();
  }
}

function openThenPulse(
  el: Element,
  setTimer: (t: ReturnType<typeof setTimeout> | null) => void,
): void {
  const chain = revealableChain(el);
  if (!chain) {
    setTimer(pulse(el));
    return;
  }
  openDisclosureChain(chain);
  setTimer(setTimeout(() => setTimer(pulse(el)), REVEAL_SETTLE_MS));
}

// One-shot reveal loses the race on a route that hasn't finished compiling or
// hydrating yet (I5): the first attempt can find nothing even though the
// target is about to appear, so a single retry covers that window.
function reveal(
  snippets: readonly string[],
  locator: string | undefined,
  setTimer: (t: ReturnType<typeof setTimeout> | null) => void,
): void {
  const el = findRevealTarget(snippets, locator);
  if (el) {
    openThenPulse(el, setTimer);
    return;
  }
  setTimer(
    setTimeout(() => {
      const retried = findRevealTarget(snippets, locator);
      if (retried) openThenPulse(retried, setTimer);
    }, REVEAL_RETRY_MS),
  );
}

// Model-controlled snippets are only ever compared against textContent, never
// written into the DOM — the injection-safety invariant for this client
// island (PUBLISHER_SANDBOX=1).
type PublisherHighlightBridgeProps = {
  // Set when mounted outside the sandbox: only parents it accepts are answered.
  isTrustedParent?: (origin: string) => boolean;
  sectionSelector?: string;
};

export function PublisherHighlightBridge({
  isTrustedParent,
  sectionSelector,
}: PublisherHighlightBridgeProps = {}) {
  const buildingRef = useRef(false);
  const snippetsRef = useRef<readonly string[]>([]);
  const loadingRef = useRef<readonly string[]>([]);

  useEffect(() => {
    if (window.parent === window) return;
    ensureStyle();

    // Without isTrustedParent nothing changes: every post goes to "*".
    let parentOrigin: string | null = isTrustedParent ? null : "*";
    const post = (message: unknown, targetOrigin = parentOrigin) => {
      if (!targetOrigin) return;
      try {
        window.parent.postMessage(message, targetOrigin);
      } catch {
        // parent may reject cross-origin postMessage; nothing to do
      }
    };
    // Every re-apply recounts, and the mutation observer fires far more often
    // than the counts actually move, so an unchanged result stays silent.
    let reported = "";
    // Places accumulate across passes because a cycling hero mounts one slide
    // at a time, so a pass that misses one shouldn't drop its row. Keyed by
    // address, and pruned by liveness below, so an accumulated place can never
    // outlive the element it points at.
    const marked = new Map<string, SnippetInstance>();
    let markedListKey = "";
    const isInstanceLive = (instance: SnippetInstance): boolean => {
      let el: Element | null = null;
      try {
        el = document.querySelector(instance.locator);
      } catch {
        return false;
      }
      if (!el || !isShown(el)) return false;
      // A positional address can resolve to a different element after the DOM
      // shifts, so confirm the words are still there before trusting it —
      // otherwise a stale row would ring unrelated copy. The hero is the one
      // exception: its identity (the heading) holds still while the word inside
      // rotates away, so it stays live on the strength of its address alone.
      if (el.closest(HERO_SELECTOR)) return true;
      const needle = foldText(instance.snippet);
      return !needle || foldText(el.textContent ?? "").includes(needle);
    };
    const applyAndReport = (list: readonly string[]) => {
      // Drop a place the moment its address stops resolving to the visible
      // element that still holds its words — a removed node, a responsive copy
      // this width hides, or an address that now points at unrelated copy — so
      // it can't linger as a stale row. A place only cycled out of view keeps a
      // live, visible identity (the hero's heading), so it survives the prune
      // and its row never blinks.
      for (const [locator, instance] of [...marked.entries()])
        if (!isInstanceLive(instance)) marked.delete(locator);
      // apply() returns the places whose words are in the DOM this pass; a
      // later pass wins on everything but a label it would blank out, so an
      // instance first seen before its heading rendered keeps the name.
      for (const instance of apply(list)) {
        const existing = marked.get(instance.locator);
        marked.set(
          instance.locator,
          existing && !instance.label ? existing : instance,
        );
      }
      const instances: SnippetInstance[] = [...marked.values()]
        .slice(0, MAX_INSTANCES)
        .map(({ snippet, locator, label }) => ({ snippet, locator, label }));
      const key = JSON.stringify(instances);
      if (key === reported) return;
      reported = key;
      post({ type: "publisher:highlight-applied", instances });
    };

    // Re-derive both layers from their source lists together, so neither can be
    // left stale when the other changes. Loading first: the settled pass then
    // sees the shimmer and yields the box to it (in-flight beats settled).
    // Settled second: an element dropped from the loading list this pass gets
    // its solid box back in the same pass, never stranded with no highlight.
    let pulseTimer: ReturnType<typeof setTimeout> | null = null;
    const setPulseTimer = (t: ReturnType<typeof setTimeout> | null) => {
      pulseTimer = t;
    };
    // Held until the user touches the page, as each hot reload re-renders the FAQ closed.
    let pendingHidden: {
      snippets: readonly string[];
      path: string;
      nth?: number;
    } | null = null;
    const dropPendingHidden = () => {
      if (!pendingHidden) return;
      pendingHidden = null;
      if (pulseTimer) clearTimeout(pulseTimer);
      pulseTimer = null;
      clearPulse();
    };
    const cancelPendingHidden = () => {
      dropPendingHidden();
      post({ type: "publisher:reveal-cancelled" });
    };
    const tryPendingHidden = () => {
      if (!pendingHidden) return;
      if (pendingHidden.path !== location.pathname) {
        cancelPendingHidden();
        return;
      }
      const pending = pendingHidden;
      const holding = () =>
        [...document.querySelectorAll(`[${ATTR}]`)].filter((candidate) => {
          const text = foldText(candidate.textContent ?? "");
          return pending.snippets.some((s) => {
            const needle = foldText(s);
            return needle && text.includes(needle);
          });
        });
      let el: Element | null;
      if (pendingHidden.nth === undefined) {
        el = findRevealTarget(pendingHidden.snippets, undefined, true);
        if (!el) return;
        // Locked to this occurrence, or a single-open accordion would flip between two items holding the edit.
        const text = foldText(el.textContent ?? "");
        pendingHidden.snippets = pendingHidden.snippets.filter((s) => {
          const needle = foldText(s);
          return needle && text.includes(needle);
        });
        pendingHidden.nth = holding().indexOf(el);
      } else {
        el = holding()[pendingHidden.nth] ?? null;
        if (!el || revealableChain(el) === null) return;
      }
      if (pulseTimer) clearTimeout(pulseTimer);
      clearPulse();
      openThenPulse(el, setPulseTimer);
    };

    const refresh = () => {
      applyLoading(loadingRef.current);
      applyAndReport(snippetsRef.current);
      tryPendingHidden();
      if (sectionSelector && buildingRef.current) {
        showBuildingPlaceholder(sectionSelector);
      }
    };

    refresh();
    // A hot reload remounts this island with no navigation, so the parent gets
    // no nav-done and never re-posts — the outline would be lost for good.
    // Announcing is the only signal the parent can act on.
    post({ type: "publisher:highlight-ready" }, "*");

    let debounce: ReturnType<typeof setTimeout> | null = null;
    // The previewed app soft-navigates and hot-reloads under the outline,
    // replacing the nodes the attribute was on — re-running on mutation keeps
    // it alive. Attribute writes aren't observed, so this can't re-trigger itself.
    const observer = new MutationObserver(() => {
      if (
        !snippetsRef.current.length &&
        !loadingRef.current.length &&
        !(sectionSelector && buildingRef.current)
      )
        return;
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => {
        debounce = null;
        refresh();
      }, REAPPLY_DEBOUNCE_MS);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // The overlay is placed in viewport coordinates, so it has to follow the
    // element as the preview scrolls or resizes. rAF coalesces the bursts of
    // scroll events into one reposition per frame.
    let positionRaf = 0;
    const schedulePosition = () => {
      if (positionRaf) return;
      positionRaf = requestAnimationFrame(() => {
        positionRaf = 0;
        positionShimmers();
      });
    };
    window.addEventListener("scroll", schedulePosition, true);
    window.addEventListener("resize", schedulePosition);

    const handler = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      if (isTrustedParent) {
        if (!isTrustedParent(event.origin)) return;
        parentOrigin = event.origin;
      }
      const data = event.data as {
        type?: unknown;
        snippets?: unknown;
        locator?: unknown;
        hiddenOnly?: unknown;
        active?: unknown;
        visible?: unknown;
      } | null;
      if (!data || typeof data !== "object") return;

      if (data.type === "publisher:highlight") {
        if (!isSafeSnippets(data.snippets)) return;
        snippetsRef.current = data.snippets;
        // Absent means painted: a sender that doesn't care about the toggle
        // gets the outline it expects.
        setPainted(data.visible !== false);
        // A different snippet list is a fresh intent — drop places the old one
        // marked so they can't survive as rows the new snippets never matched.
        // The same list again (a reload or a resize re-request) keeps the set
        // and only re-prunes, so a cycling hero's row doesn't blink.
        const listKey = JSON.stringify(data.snippets);
        if (listKey !== markedListKey) {
          marked.clear();
          markedListKey = listKey;
        }
        // The parent drops its instance list whenever the frame moves, so an
        // explicit request has to be answered even when nothing here changed —
        // otherwise a reload that leaves this island mounted (or a soft nav
        // back to the same page) silently strands the parent with no rows.
        reported = "";
        refresh();
        return;
      }
      if (data.type === "publisher:shimmer") {
        if (!isSafeSnippets(data.snippets)) return;
        loadingRef.current = data.snippets;
        refresh();
        return;
      }
      if (data.type === "publisher:building") {
        if (typeof data.active !== "boolean") return;
        buildingRef.current = data.active;
        if (data.active) showBuildingPlaceholder(sectionSelector);
        else hideBuildingPlaceholder();
        return;
      }
      if (data.type === "publisher:reveal" && isSafeSnippets(data.snippets)) {
        if (data.hiddenOnly === true) {
          if (!data.snippets.length) {
            dropPendingHidden();
            return;
          }
          pendingHidden = { snippets: data.snippets, path: location.pathname };
          tryPendingHidden();
          return;
        }
        pendingHidden = null;
        if (pulseTimer) clearTimeout(pulseTimer);
        clearPulse();
        const locator = isSafeLocator(data.locator) ? data.locator : undefined;
        reveal(data.snippets, locator, setPulseTimer);
      }
    };
    window.addEventListener("message", handler);

    const USER_INPUT = ["pointerdown", "wheel", "keydown", "touchstart"];
    const onUserInput = (event: Event) => {
      // Sent even with nothing pending: the parent may still be waiting on the diff to arm one.
      if (event.isTrusted) cancelPendingHidden();
    };
    for (const type of USER_INPUT)
      window.addEventListener(type, onUserInput, {
        capture: true,
        passive: true,
      });

    return () => {
      window.removeEventListener("message", handler);
      for (const type of USER_INPUT)
        window.removeEventListener(type, onUserInput, { capture: true });
      window.removeEventListener("scroll", schedulePosition, true);
      window.removeEventListener("resize", schedulePosition);
      observer.disconnect();
      if (debounce) clearTimeout(debounce);
      if (pulseTimer) clearTimeout(pulseTimer);
      if (positionRaf) cancelAnimationFrame(positionRaf);
      clear();
      clearLoading();
      setPainted(true);
      removeShimmerLayer();
      hideBuildingPlaceholder();
      removeStyle();
    };
  }, [isTrustedParent, sectionSelector]);

  return null;
}
